<script setup>
import BaseCustomerDisplay from '@/components/base/BaseCustomerDisplay.vue'
import BaseProductDisplay from '@/components/base/BaseProductDisplay.vue'

import { computed, ref, defineProps } from 'vue'
import { formatCurrency } from '@/helpers/stringHelpers'

const props = defineProps({
  prices: {
    type: Array,
    default: () => []
  },

  // "customer" means each chip displays a customer.
  // "product" means each chip displays a product.
  displayType: {
    type: String,
    required: true,
    validator: (value) => ['customer', 'product'].includes(value)
  },

  maxVisible: {
    type: Number,
    default: 8
  },

  maxNameLength: {
    type: Number,
    default: 20
  }
})

const isExpanded = ref(false)

const visiblePrices = computed(() => {
  if (isExpanded.value) {
    return props.prices
  }

  return props.prices.slice(0, props.maxVisible)
})

const hiddenCount = computed(() => {
  if (isExpanded.value) return 0

  return Math.max(props.prices.length - props.maxVisible, 0)
})

const hasOverflow = computed(() => {
  return props.prices.length > props.maxVisible
})

const toggleChipText = computed(() => {
  return isExpanded.value ? 'Show less' : `+${hiddenCount.value} more`
})

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}
</script>

<template>
  <div class="special-pricing-chips-cell">
    <template v-if="prices.length">
      <v-chip
        v-for="price in visiblePrices"
        :key="price.id"
        small
        label
        class="special-price-chip me-1 mb-1"
      >
        <span class="price-chip-name">
          <BaseCustomerDisplay
            v-if="displayType === 'customer'"
            :customer-id="price.customer_id"
            :max-name-length="maxNameLength"
          />

          <BaseProductDisplay
            v-else
            :product-id="price.productlist_id"
            :max-name-length="maxNameLength"
          />
        </span>

        <span class="price-chip-value">
          {{ formatCurrency(price.price_kg) }}
        </span>
      </v-chip>

      <v-chip
        v-if="hasOverflow"
        small
        label
        class="more-prices-chip me-1 mb-1"
        @click.stop="toggleExpanded"
      >
        {{ toggleChipText }}
      </v-chip>
    </template>

    <span v-else class="text-muted">-</span>
  </div>
</template>

<style scoped>
.special-pricing-chips-cell {
  line-height: 1.6;
}

:deep(.special-price-chip.v-chip) {
  height: 24px;
  background-color: #eef2f6 !important;
  border: 1px solid #d9e1ea !important;
  border-radius: 4px;
}

:deep(.special-price-chip .v-chip__content) {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 250px;
  overflow: hidden;
}

.price-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price-chip-value {
  flex: 0 0 auto;
  font-weight: 600;
  padding-left: 6px;
  border-left: 1px solid rgba(0, 0, 0, 0.14);
}

:deep(.more-prices-chip.v-chip) {
  height: 24px;
  background-color: #ffffff !important;
  border: 1px solid #c8d6e3 !important;
  font-weight: 600;
  cursor: pointer;
}

:deep(.more-prices-chip.v-chip:hover) {
  background-color: #eaf3ff !important;
  border-color: #90caf9 !important;
}
</style>
